import { descendantIds, findNode, type TreeLike } from "./tree-model";

/** The shape product-api returns: one direct count per category. */
export interface DirectCategoryCount {
  categoryId: string;
  directCount: number;
}

/**
 * Most ids we are willing to put in the `category_id` query string.
 *
 * Filtering a branch means naming every category in it, and each id is a
 * 36-character UUID plus a separator. Proxies commonly cap the request line
 * near 8 KB, so ~200 ids (~7.4 KB) is the safe ceiling. The largest taxonomy
 * measured on staging needed 73 ids for its widest branch, so this is well
 * clear of real trees; above it we filter on the category alone rather than
 * send a request that would be rejected outright.
 */
export const CATEGORY_FILTER_ID_LIMIT = 200;

/**
 * Total products per category, counting everything beneath it.
 *
 * product-api deliberately returns only direct counts — rolling up in SQL
 * costs ~25x more than summing over the tree the caller already holds — so
 * the rollup happens here. Every category in the tree gets an entry, so a
 * category with nothing beneath it reads 0 rather than blank.
 */
export function rollUpCounts<T extends TreeLike<T>>(
  roots: T[],
  counts: readonly DirectCategoryCount[],
): Map<string, number> {
  const direct = new Map(counts.map((c) => [c.categoryId, c.directCount]));
  const totals = new Map<string, number>();

  const visit = (node: T): number => {
    const own = direct.get(node.id) ?? 0;
    const total = node.children.reduce((sum, child) => sum + visit(child), own);
    totals.set(node.id, total);
    return total;
  };

  roots.forEach(visit);
  return totals;
}

/**
 * The category ids to filter on when the user picks `categoryId`: the
 * category itself plus everything beneath it, so choosing a parent shows the
 * whole branch and matches the rolled-up count shown beside it.
 *
 * `truncated` means the branch was too large to name in a query string and
 * the caller is getting direct assignments only — the UI has to say so,
 * because the result will not match the count.
 */
export function expandCategoryIds<T extends TreeLike<T>>(
  roots: T[],
  categoryId: string,
): { ids: string[]; truncated: boolean } {
  const node = findNode(roots, categoryId);
  if (!node) return { ids: [categoryId], truncated: false };

  const ids = [node.id, ...descendantIds(node)];
  if (ids.length > CATEGORY_FILTER_ID_LIMIT) {
    return { ids: [node.id], truncated: true };
  }
  return { ids, truncated: false };
}
